/**
 * Kiểu dữ liệu khớp 1-1 với hợp đồng API của server (server/src/api/*.ts).
 * Sửa ở server thì phải sửa ở đây — đừng đoán, hãy đọc lại route tương ứng.
 */

/* ------------------------------------------------------------------ quyền */

export type Role = 'SUPER_ADMIN' | 'ADMIN' | 'MANAGER' | 'OPERATOR' | 'VIEWER';

/** Thứ bậc quyền — giống hệt RANK trong server/src/api/auth.ts */
export const ROLE_RANK: Record<Role, number> = {
  VIEWER: 0,
  OPERATOR: 1,
  MANAGER: 2,
  ADMIN: 3,
  SUPER_ADMIN: 4,
};

export const ROLE_LABEL: Record<Role, string> = {
  SUPER_ADMIN: 'Quản trị hệ thống',
  ADMIN: 'Quản trị dự án',
  MANAGER: 'Quản lý vận hành',
  OPERATOR: 'Vận hành viên',
  VIEWER: 'Chỉ xem',
};

export const ROLE_HINT: Record<Role, string> = {
  SUPER_ADMIN: 'Toàn quyền, tạo được dự án và tài khoản quản trị',
  ADMIN: 'Thêm/sửa/xoá tuyến, tủ, người dùng trong dự án',
  MANAGER: 'Điều khiển + cài lịch, chiến lược. Không sửa cấu trúc',
  OPERATOR: 'Chỉ bật/tắt ngõ ra, không sửa lịch',
  VIEWER: 'Chỉ xem, mọi nút điều khiển đều bị khoá',
};

/* ---------------------------------------------------------- người dùng */

export interface AuthUser {
  id: string;
  username: string;
  fullName: string;
  email?: string | null;
  phone?: string | null;
  role: Role;
  projectId: string | null;
  mustChangePwd: boolean;
  /*
   * ➕ `P-6` `B4` — BA TRƯỜNG NAY ĐI CÙNG `/api/auth/me`, và giao diện ẩn/hiện nút theo chúng
   *   chứ không theo `role` nữa.
   * ⚠ Cả ba đều **có thể VẮNG** khi máy chủ là bản cũ hơn — và `undefined` ở đây phải đọc là
   *   *"máy chủ không nói"*, KHÔNG phải *"không có quyền nào"*. Chỗ đọc (`lib/auth.tsx`) xử lý
   *   đúng một chỗ; đừng rải `?? []` khắp nơi, vì mỗi chỗ rải là một chỗ chọn nghĩa khác.
   */
  expiresAt?: string | null;
  quyen?: string[];
  createdById?: string | null;
  routeAccess?: RouteAccessItem[];
  /*
   * ➕ 10/09 (lượt 58) — TÊN người tạo, để băng khoá nói được *"hỏi ai"*.
   *
   * ⚠ BA nghĩa, ba giá trị khác nhau — và Luật số 0 bắt tôi giữ chúng tách ra:
   *     `undefined`  máy chủ bản cũ, KHÔNG NÓI GÌ  → băng lùi về câu chung chung
   *     `null`       tài khoản GỐC, không có người tạo (SUPER_ADMIN thật, hoặc ca `@jlee`)
   *     `{username}` có tên → băng in `@<tên>`
   *   Gộp `null` với `undefined` bằng một `?? ''` là bỏ mất đúng cái phân biệt mà mức 0 của
   *   cây tài khoản (việc 2d) đang dựa vào.
   */
  nguoiTao?: { username: string } | null;
}

export interface LoginResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  user: AuthUser;
}

export interface RouteAccessItem {
  routeId: string;
  canControl: boolean;
  canEditSchedule: boolean;
}

export interface UserRow {
  id: string;
  username: string;
  fullName: string;
  email: string | null;
  phone: string | null;
  role: Role;
  isActive: boolean;
  projectId: string | null;
  lastLoginAt: string | null;
  createdAt: string;
  routeAccess: RouteAccessItem[];
  /* ➕ `B4`: cột "Hết hạn" · "Người tạo" · bảng tick trong form. `null` ở `expiresAt` là
   * VĨNH VIỄN — một mốc thật, không phải "chưa đặt". */
  expiresAt: string | null;
  quyen: string[];
  createdById: string | null;
}

/* ------------------------------------------------------------ cây tài sản */

export type CabinetStatus = 'UNPROVISIONED' | 'ONLINE' | 'OFFLINE' | 'MAINTENANCE';
export type SwitchPos = 'OFF' | 'MANUAL' | 'AUTO';
export type OutputMode = 'TIMER' | 'SEASON' | 'ALWAYS_ON' | 'ALWAYS_OFF';

export const MODE_LABEL: Record<OutputMode, string> = {
  TIMER: 'Theo timer',
  SEASON: 'Theo mùa (mặt trời)',
  ALWAYS_ON: 'Luôn bật',
  ALWAYS_OFF: 'Luôn tắt',
};

export const STATUS_LABEL: Record<CabinetStatus, string> = {
  UNPROVISIONED: 'Chưa kết nối lần nào',
  ONLINE: 'Đang kết nối',
  OFFLINE: 'Mất kết nối',
  MAINTENANCE: 'Đang bảo trì',
};

export const SWITCH_LABEL: Record<SwitchPos, string> = {
  OFF: 'TẮT (cắt tay)',
  MANUAL: 'BẰNG TAY',
  AUTO: 'TỰ ĐỘNG',
};

/** Ngõ ra rút gọn trong cây (GET /api/tree) */
export interface TreeOutput {
  id: string;
  index: number;
  name: string;
  isOn: boolean;
  enabled: boolean;
  mode: OutputMode;
  manualHold: boolean;
}

export interface TreeCabinet {
  id: string;
  name: string;
  code: string;
  hardwareId: string;
  deviceId: string | null;
  status: CabinetStatus;
  switchPos: SwitchPos | null;
  lat: number | null;
  lng: number | null;
  /// GD-17 (bản đồ Tổng quan): máy chủ thêm trường CHỈ ĐỌC này vào `/api/tree`. Chỉ nói NGUỒN
  /// toạ độ (tủ tự đo / nhập tay) trong popup; chấm vẽ theo lat/lng hợp lệ (GD-17 vòng 2).
  gpsFix?: boolean;
  protocolVer: number;
  firmwareVersion: string | null;
  /** TK-19: bản firmware THẬT (git) + đơn vị công suất máy chủ suy từ model công tơ tủ khai (null = không hiện). */
  fwGit?: string | null;
  fwDirty?: boolean | null;
  meterModel?: number | null;
  donViCongSuat?: 'W' | 'kW' | null;
  lastSeenAt: string | null;
  outputs: TreeOutput[];
}

export interface TreeRoute {
  id: string;
  /// ⚠ TÊN TRƠ — chỉ dùng cho ô sửa và ô lọc. **KHÔNG hiện một mình** (chốt YC-21).
  /// Chỗ hiện cho người đọc là `tenDayDu`.
  name: string;
  code: string | null;
  /// TK-21: hai cấp dưới tên — Xã/Phường, Tỉnh/Thành phố. Không còn cấp huyện.
  phuong: string | null;
  tinh: string | null;
  /// Tên + địa chỉ, do MÁY CHỦ dựng bằng `tenDayDu()`. Giao diện KHÔNG tự ghép:
  /// hai chỗ ghép là hai chỗ sẽ trôi khỏi nhau.
  tenDayDu: string;
  cabinetCount: number;
  onlineCount: number;
  /** TK-38 việc 2c: trạng thái ngõ ra tổng hợp theo tuyến (máy chủ, từ ngõ TỦ BÁO); vắng/null = chưa rõ */
  ngo?: { trangThai: 'bat' | 'tat' | 'mot-phan' | null; tuBat: number; tuNoi: number; tuMat: number } | null;
  cabinets: TreeCabinet[];
}

export interface RouteRow {
  id: string;
  projectId: string;
  /// ⚠ TÊN TRƠ — xem ghi chú ở `TreeRoute.name`. Hiện cho người đọc thì dùng `tenDayDu`.
  name: string;
  /// Mã gợi nhớ, có thể rỗng — sau YC-21 mã KHÔNG còn là danh tính của tuyến chiếu sáng.
  code: string | null;
  /// TK-21: Xã/Phường BẮT BUỘC khi tạo/sửa (tuyến cũ có thể còn trống tới khi được sửa).
  phuong: string | null;
  tinh: string | null;
  note: string | null;
  orderNo: number;
  tenDayDu: string;
  _count?: { cabinets: number };
}

/** Tủ còn thiếu tên / thiếu đường — `GET /api/cabinets/danh-sach-cho`. */
export type Thieu = 'chua-dat-ten' | 'chua-gan-duong' | 'ca-hai';

export const NHAN_THIEU: Record<Thieu, string> = {
  'chua-dat-ten': 'Chưa đặt tên',
  'chua-gan-duong': 'Chưa gán tuyến',
  'ca-hai': 'Chưa đặt tên · chưa gán tuyến',
};

/** TK-17 — vòng đời tài sản của tủ. Khác `CabinetStatus` (kết nối). */
export type VongDoi = 'CHO_DUYET' | 'DA_DUYET' | 'CHAN' | 'NGHI_HUU';

/** TK-17: một hàng ở khối "Tủ chờ duyệt" / "Tủ đã chặn" (`/api/cabinets/cho-duyet`, `/bi-chan`). */
export interface TuChoDuyet {
  id: string;
  name: string;
  hardwareId: string;
  deviceId: string | null;
  status: CabinetStatus;
  lastSeenAt: string | null;
  /** Lần đầu thấy — lúc tủ tự REGISTER. */
  createdAt: string;
  firmwareVersion: string | null;
  vongDoi: VongDoi;
  vongDoiLuc: string | null;
  online: boolean;
}

export interface TuBiChanRow {
  hardwareId: string;
  chanLuc: string;
  chanBoi: string | null;
  lyDo: string | null;
  /** `null` = MAC bị chặn nhưng không còn hàng tủ nào (máy chủ vẫn nhớ, không tạo lại). */
  tu: TuChoDuyet | null;
}

export interface TuChoXuLy {
  id: string;
  /// Một câu để người nhớ. ⛔ KHÔNG phải danh tính, KHÔNG unique — hai tủ trùng tên là
  /// bình thường (khác hẳn tên tuyến chiếu sáng). Trạng thái "đã đặt tên" đọc ở `datTenLuc`,
  /// TUYỆT ĐỐI không dò chuỗi `AUTO-`.
  name: string;
  /// TK-17: mã là của TUYẾN — tủ chưa gán tuyến thì `null`.
  code: string | null;
  /// 🔴 Danh tính vật lý, không ai sửa. Cũng là cột duy nhất không tái tạo được nếu mất.
  hardwareId: string;
  deviceId: string | null;
  status: CabinetStatus;
  lastSeenAt: string | null;
  /// "Lần đầu thấy" — lúc tủ tự khai với máy chủ.
  createdAt: string;
  lat: number | null;
  lng: number | null;
  online: boolean;
  datTenLuc: string | null;
  datTenBoi: string | null;
  routeId: string | null;
  /// Tên đầy đủ tuyến chiếu sáng (tên + địa chỉ) do máy chủ dựng, `null` nếu chưa gán.
  tenDuong: string | null;
  /// Máy chủ suy từ ĐÚNG hai cột mà bộ lọc dùng — giao diện không tự đoán lại.
  thieu: Thieu;
}

export interface ProjectRow {
  id: string;
  name: string;
  code: string;
  address: string | null;
  note: string | null;
}

export interface MeterSample {
  at: string;
  v1: number | null; v2: number | null; v3: number | null;
  i1: number | null; i2: number | null; i3: number | null;
  p1: number | null; p2: number | null; p3: number | null;
  a1: number | null; a2: number | null; a3: number | null;
  power: number | null;
  total: number | null;
}

/** Ngõ ra đầy đủ trong GET /api/cabinets/:id (kèm timer từ DB) */
export interface CabinetOutput {
  id: string;
  cabinetId: string;
  index: number;
  name: string;
  enabled: boolean;
  mode: OutputMode;
  sunsetOffsetMin: number;
  sunriseOffsetMin: number;
  isOn: boolean;
  manualHold: boolean;
  /*
   * NV108 — `OutputRuntime.nguonGiuQuyen`. **Không bắt buộc, và đó là điểm chính**: đường
   * `GET /api/cabinets/:id` dựng từ CSDL, mà CSDL **chưa có cột này** (lược đồ đổi cần một
   * lượt chuyển, `P-2` đang mở). Trường chỉ tới theo **khung sống** qua WebSocket.
   * ⇒ Vắng ⇒ `docNguonGiu()` đọc ra `'chua-biet'`, KHÁC HẲN `'khong-ai'`. Đừng đặt `= 0` mặc
   *   định ở đây: làm thế là biến *"chưa nghe tủ nói"* thành *"tủ nói không ai giữ"*.
   */
  nguonGiuQuyen?: number;
  activeTimer: number | null;
  nextOnMin: number | null;
  nextOffMin: number | null;
  lastChangeAt: string | null;
  timers: {
    id: string;
    index: number;
    enabled: boolean;
    onMin: number;
    offMin: number;
    daysOfWeek: string;
    specialDays: SpecialDay[];
  }[];
}

export interface Alarm {
  id: string;
  cabinetId: string;
  code: string;
  output: number | null;
  active: boolean;
  message: string | null;
  /**
   * MỨC CỦA LẦN NÀY (YC-29). `null` = không có mức riêng ⇒ đọc `BANG_MUC` theo mã.
   * ⛔ Đừng nới thành `number` rồi `?? 2`: vắng mức riêng KHÁC mức 2.
   */
  mucDo: 1 | 2 | null;
  /**
   * ═══════════════════════════════════════════════════════════════════════════════════════
   * ĐỘ TƯƠI CỦA CHÍNH CẢNH BÁO NÀY (YC-44) — `null` = mã này KHÔNG sống theo khung sức khoẻ.
   *
   * 🔴 Bốn mã `METER_TIMEOUT` · `RTC_INVALID` · `GPS_LOST` · `NVS_ERROR` chỉ đóng được khi
   *   một khung `DeviceHealth` về. Khung ấy có thể **rất lâu không về** — và khi đó hàng vẫn
   *   `active` còn màn hình vẫn khẳng định như thể vừa đo xong.
   *
   * ⛔ `'chua-do-lan-nao'` KHÁC `'qua-han'`, và cả hai đều khác `'moi'`. Gộp lại là mất đúng
   *   thứ người trực cần: *"chưa bao giờ đo"* thì đi hỏi vì sao tủ không trả lời, còn
   *   *"đo lúc 17:45 rồi thôi"* thì đi xem từ 17:45 có chuyện gì.
   * ═══════════════════════════════════════════════════════════════════════════════════════
   */
  doTuoi: { tuoi: 'moi' } | { tuoi: 'qua-han'; lucNhanMs: number } | { tuoi: 'chua-do-lan-nao' } | null;
  raisedAt: string;
  clearedAt: string | null;
  ackedById: string | null;
  ackedAt: string | null;
  /** TK-25: lần CUỐI máy chủ còn thấy việc này (một việc = một hồ sơ). `null` = hàng cũ / chưa thấy lại. */
  lastSeenAt?: string | null;
  /** TK-25: ai bấm "Đã xử lý"; `null` khi máy chủ tự đóng hoặc hàng cũ. */
  resolvedById?: string | null;
  /** TK-25: LÚC người bấm "Đã xử lý" (lời khai). Khác `clearedAt` (lúc hàng ĐÓNG) — mã tự đóng thì hai mốc khác nhau. */
  resolvedAt?: string | null;
  /** TK-25: AI đóng hàng — `'nguoi'` (mã không tự đóng) · `'tu-dong'` (tủ/máy chủ thấy hết) · `null` = chưa đóng / hàng cũ. */
  resolvedHow?: 'nguoi' | 'tu-dong' | null;
  /** TK-25: ghi chú người trực kèm lần bấm gần nhất. */
  note?: string | null;
  /** TK-25: tên người ghi nhận / xử lý — máy chủ tra; `null` = tài khoản đã xoá hoặc không có. */
  ackedBy?: { username: string; fullName: string } | null;
  resolvedBy?: { username: string; fullName: string } | null;
  cabinet?: { id: string; code: string | null; name: string; routeId: string | null };
  /** TK-31 mốc 3: vấn đề của MỘT đèn NEMA (addr); null = của tủ / card. Máy chủ trả nguyên cột. */
  nemaAddr?: number | null;
  /** TK-31 22/10: tên người đọc của đèn ấy ("Trụ 04" / "Đèn 3") do máy chủ dựng (`sttTheoAddr`). Vắng ⇒ không bịa. */
  nemaTen?: string | null;
}

export interface CabinetDetail {
  id: string;
  routeId: string;
  name: string;
  /** TK-17: `null` khi tủ chưa thuộc tuyến nào (chờ duyệt, bị chặn, vừa gỡ tuyến). */
  code: string | null;
  hardwareId: string;
  deviceId: string | null;
  numOutputs: number;
  /**
   * ⛔ ĐỪNG DỰNG GIAO DIỆN THEO TRƯỜNG NÀY. Firmware ghi cứng `2` khi đăng ký
   * (`websocket_app.cpp:474` — dev_device tự khai ở YC-32), nên nó là một con số **chưa ai
   * đo**. Dùng `soPha` bên dưới; xem `soPhaDangBiet()` phía máy chủ.
   */
  numPhase: number;
  /**
   * Số pha ĐANG BIẾT, đọc từ `protectEn` bit 6-7 của phiên đang mở.
   * `null` = **chưa biết** — và "chưa biết" thì vẽ đủ ba cột, không được đoán.
   */
  soPha?: 1 | 2 | 3 | null;
  lat: number | null;
  lng: number | null;
  address: string | null;
  note: string | null;
  protocolVer: number;
  supportsAck: boolean;
  supportsPerOutputSchedule: boolean;
  supportsSpecialDays: boolean;
  supportsSeasonOffset: boolean;
  supportsDim: boolean;
  supportsOta: boolean;
  maxTimersPerOutput: number;
  firmwareVersion: string | null;
  /** TK-19 */
  fwNhan?: string | null;
  fwGit?: string | null;
  fwDirty?: boolean | null;
  fwLuc?: string | null;
  meterModel?: number | null;
  donViCongSuat?: 'W' | 'kW' | null;
  /** TK-22 B — ngưỡng CẢNH BÁO quá dòng (A); `null` = không cảnh báo. */
  nguongCanhBaoQuaDongA?: number | null;
  legacyModeInverted: boolean;
  status: CabinetStatus;
  lastSeenAt: string | null;
  lastRebootAt: string | null;
  switchPos: SwitchPos | null;
  rssi: number | null;
  gpsFix: boolean;
  rtcDriftSec: number | null;
  sunriseMin: number | null;
  sunsetMin: number | null;
  /// 🔴 RỖNG được. Tủ vừa cắm chưa gán tuyến, và tủ vừa bị gỡ khỏi tài khoản —
  /// hai trạng thái THẬT (chốt YC-21 vòng 2 mục C2). Kiểu cũ khai không-rỗng là nói dối,
  /// và lời nói dối đó chính là 5 lỗi TS18047 lúc di trú.
  route: RouteRow | null;
  outputs: CabinetOutput[];
  alarms: Alarm[];
  online: boolean;
  lastMeter: MeterSample | null;
  /**
   * ═════════════════════════════════════════════════════════════════════════════════════
   * YC-34 lớp 1 — MẪU ĐO CÒN DÙNG ĐƯỢC KHÔNG. Máy chủ chấm, giao diện chỉ đọc.
   *
   * ⛔ `lastMeter` KHÁC `lastMeter còn dùng được`. Công tơ chết thì chip **ngừng gửi** chứ
   *   không gửi số 0 — hàng cuối cùng nằm lại trong CSDL và đông lạnh ở đó. Vẽ nó ra là vẽ
   *   một con số hợp lý của quá khứ, và một con số hợp lý thì **không ai đi kiểm tra**
   *   (dev_device YC-34: nguy hơn số 0, vì số 0 còn làm người ta nghi).
   *
   * ⛔ ĐỪNG tự chấm hạn ở đây. Cần CẢ HAI vế — tuổi mẫu < `METER_HET_TUOI_MS` **và**
   *   `DeviceHealth.modbusOnline = 1` — mà giao diện không có vế thứ hai, và hằng số
   *   35 000 ms là hằng của CHIP (`dev_config_para.h:153`), không được sao chép sang đây.
   *
   * `undefined` = máy chủ đời cũ chưa trả trường này ⇒ xử như **chưa biết**, không xử như
   * "vẽ được" (L62: vắng không phải là một câu trả lời).
   * ═════════════════════════════════════════════════════════════════════════════════════
   */
  soDo?:
    | {
        ve: true;
        tuoiMs: number;
        /**
         * Kết luận này còn sống thêm bao lâu KỂ TỪ LÚC NHẬN (YC-35).
         * ⛔ Một kết luận không có hạn là một kết luận đông lạnh trong bộ nhớ đệm trình duyệt.
         *   Xem `lib/hansudung.ts`.
         */
        hanConLaiMs: number;
      }
    | {
        ve: false;
        vi: 'chua-co-mau' | 'qua-han' | 'cong-to-offline' | 'dong-ho-tu-lech';
        tuoiMs: number | null;
        /// Câu cho người trực đọc — do MÁY CHỦ dựng. Đừng dựng bảng thứ hai từ `vi` ở đây.
        cau: string;
        hanConLaiMs: 0;
      };
}

/* ------------------------------------------------------------- tổng quan */

export interface OverviewRouteCard {
  /// 🔴 RỖNG = thẻ gom "tủ chưa gán tuyến chiếu sáng" (tủ vừa cắm, hoặc tủ vừa bị gỡ khỏi tài khoản).
  /// Không có đường thì KHÔNG có màn hình điều khiển toàn tuyến để mở — chỗ nào dựng
  /// đường dẫn từ trường này phải kiểm rỗng trước, đừng để ra `?tuyen=null`.
  routeId: string | null;
  /// Tên + địa chỉ, do máy chủ dựng bằng `tenDayDu()`. Cố ý KHÔNG đặt tên là `name`:
  /// gọi là `name` thì chỗ đọc không biết nó đã kèm địa chỉ hay chưa.
  tenDayDu: string;
  /// Mã gợi nhớ, có thể rỗng — sau YC-21 mã không còn là danh tính nên không bắt buộc nhập.
  code: string | null;
  total: number;
  online: number;
  /**
   * Số ngõ ra đang bật, **chỉ đếm trên những tủ ĐANG NỐI** (luật YC-35).
   * ⛔ `Output.isOn` đến từ `CabinetState.outputs[].on`, và kênh ấy KHÔNG có chu kỳ — tủ mất
   *   kết nối thì cột ấy đông lạnh ở lần liên lạc cuối. Cửa đúng là kết nối, không phải tuổi.
   */
  on: number;
  /// Bao nhiêu tủ trong tuyến KHÔNG nói thay được (mất kết nối). `0` = con số `on` nói cho cả tuyến.
  khongDem?: number;
}

export interface Overview {
  cabinets: number;
  online: number;
  offline: number;
  /** TK-25: số tuyến trong phạm vi tài khoản (kể cả tuyến chưa có tủ). Thiếu = máy chủ cũ. */
  soTuyen?: number;
  /** TK-25: mốc máy chủ dựng số liệu (ISO) — "lấy mẫu HH:mm:ss". */
  lucLayMau?: string;
  sessions: number;
  /// ⚠ Chỉ tính trên tủ ĐANG NỐI — xem `khongDem` / `soTuKhongDem` trước khi đọc như số toàn hệ.
  outputsOn: number;
  outputsTotal: number;
  activeAlarms: number;
  /// 🔴 TK-22 A — ĐƠN VỊ W, đã quy đổi theo công tơ từng tủ. `null` = KHÔNG tủ nào cộng được.
  /// Một tổng trên tập rỗng không phải số 0.
  tongCongSuatW: number | null;
  /// Bao nhiêu tủ thật sự góp mẫu đo còn hạn.
  soTuGopDuoc?: number;
  /// Trong số ấy, bao nhiêu tủ ĐƯỢC CỘNG vào `tongCongSuatW`.
  soTuTinhCongSuat?: number;
  /// Có số nhưng chưa biết công tơ (W hay kW) ⇒ KHÔNG cộng.
  soTuChuaRoDonVi?: number;
  /// Có mẫu còn hạn nhưng không có số công suất.
  soTuKhongCoSoCongSuat?: number;
  /// Bao nhiêu tủ mất kết nối nên KHÔNG được tính vào `outputsOn` / `outputsTotal`.
  soTuKhongDem?: number;
  byRoute: OverviewRouteCard[];
}

/* ------------------------------------------------------------------ lệnh */

// GD-14 vòng 2 — thêm `SUPERSEDED`: `enum CommandState` của Prisma có, máy chủ ghi (deviceGateway),
// kiểu web thiếu ⇒ huy hiệu từng in mã trần. `kiem_loi_nguoi.ts` đọc `schema.prisma` để khớp.
export type CommandState = 'QUEUED' | 'SENT' | 'ACKED' | 'FAILED' | 'TIMEOUT' | 'SKIPPED_OFFLINE' | 'SUPERSEDED';

export const COMMAND_STATE_LABEL: Record<CommandState, string> = {
  QUEUED: 'Đang xếp hàng',
  SENT: 'Đã gửi xuống tủ',
  ACKED: 'Tủ đã xác nhận',
  FAILED: 'Thất bại',
  TIMEOUT: 'Quá hạn chờ xác nhận',
  SKIPPED_OFFLINE: 'Bỏ qua — tủ mất kết nối',
  SUPERSEDED: 'Đã thay bằng lệnh mới',
};

export interface Command {
  id: string;
  cabinetId: string;
  kind: string;
  payload: unknown;
  state: CommandState;
  wireVer: number;
  ackMode: 'INFERRED' | 'EXPLICIT';
  detail: string | null;
  issuedById: string | null;
  sentAt: string | null;
  ackedAt: string | null;
  createdAt: string;
}

export interface DeviceLogRow {
  id: string;
  cabinetId: string;
  level: number;
  tag: string | null;
  message: string;
  at: string;
}

export interface AuditRow {
  id: string;
  userId: string | null;
  action: string;
  target: string | null;
  detail: unknown;
  ip: string | null;
  at: string;
  user: { username: string; fullName: string } | null;
  /**
   * CẤP CỦA THAO TÁC — do MÁY CHỦ xếp (`services/capthaotac.ts`), giao diện chỉ vẽ.
   * ⛔ `null` = mã này **chưa ai xếp cấp** (thường là mã do một bản máy chủ mới hơn ghi ra).
   *   KHÔNG phải cấp 3. Xếp một thao tác chưa phân loại xuống cấp thấp nhất là giấu nó đi.
   */
  cap?: 1 | 2 | 3 | null;
}

/** Kết quả điều khiển toàn tuyến — POST /api/routes/:id/control */
export interface RouteControlResultItem {
  cabinetId: string;
  code: string;
  name: string;
  ok: boolean;
  state: CommandState | 'ERROR';
  commandId?: string;
  error?: string;
}

export interface RouteControlResult {
  total: number;
  sent: number;
  offline: number;
  failed: number;
  results: RouteControlResultItem[];
}

/* ------------------------------------------------------------------ lịch */

export interface SpecialDay {
  /** ngày trong tháng 0..31 (0 = không dùng) */
  d: number;
  /** tháng 0..12 (0 = mọi tháng) */
  m: number;
}

export interface FullTimer {
  index: number;          // 0..47
  enabled: boolean;
  onMin: number;          // phút tính từ 00:00, 0..1439
  offMin: number;
  /** 7 ký tự '0'/'1' — index 0 = Chủ nhật … index 6 = Thứ 7 */
  daysOfWeek: string;
  specialDays: SpecialDay[];
}

export interface FullOutput {
  index: number;          // 1..3
  name: string;
  enabled: boolean;
  mode: OutputMode;
  sunsetOffsetMin: number;
  sunriseOffsetMin: number;
  timers: FullTimer[];    // đủ 48 phần tử
}

export interface FullSchedule {
  deviceId: string;
  schemaVer: number;
  revision: string;
  outputs: FullOutput[];  // đúng 3
}

export interface V1Preview {
  usedSlots: number;
  losses: string[];
}

/**
 * BẢN KHAI CỦA TỦ — `Capability` tủ tự gửi mỗi lần nối.
 *
 * 🔴 Giao diện tắt ô nào là theo đây, KHÔNG suy từ `protocolVer` và KHÔNG đọc hợp đồng.
 * Hợp đồng có thể đi trước firmware; bản khai thì không.
 */
export interface KhaNangTu {
  ack: boolean;
  lichTungNgo: boolean;
  ngayDacBiet: boolean;
  offsetMua: boolean;
  dim: boolean;
  ota: boolean;
  maxTimerMoiNgo: number;
  firmwareVersion: string | null;
}

export interface ScheduleResponse {
  /** bảng của máy chủ = bản đang soạn (có thể chưa đẩy xuống tủ) */
  schedule: FullSchedule;
  /** TK-37: lịch TỦ ĐANG CHẠY (lệnh đã ACK / tủ tự báo); null = chưa biết */
  dangChay: { outputs: FullSchedule['outputs']; luc: (string | null)[] } | null;
  /** TK-37: ngõ có bản nháp khác lịch tủ đang chạy */
  ngoNhapKhac: number[];
  protocolVer: number;
  /// Bản khai của tủ — xem `KhaNangTu`. Tắt ô theo cái này, không theo `protocolVer`.
  kha: KhaNangTu;
  v1Preview: V1Preview;
}

export interface ScheduleHistoryRow {
  id: string;
  revision: string;
  state: 'DRAFT' | 'PENDING' | 'APPLIED' | 'FAILED' | 'SUPERSEDED';
  wireVer: number;
  lossyNote: string | null;
  createdAt: string;
  appliedAt: string | null;
}

export const SCHEDULE_STATE_LABEL: Record<ScheduleHistoryRow['state'], string> = {
  DRAFT: 'Bản nháp',
  PENDING: 'Đã đẩy, chờ tủ xác nhận',
  APPLIED: 'Tủ đã áp dụng',
  FAILED: 'Thất bại',
  SUPERSEDED: 'Đã bị bản mới thay thế',
};

/* ------------------------------------------------------------ chiến lược */

export type StrategyKind = 'FIXED_TIME' | 'SEASON' | 'SEASON_TRIM' | 'MIDNIGHT_DIM' | 'CUSTOM';

export const STRATEGY_KIND_LABEL: Record<StrategyKind, string> = {
  FIXED_TIME: 'Giờ cố định',
  SEASON: 'Theo mùa (hoàng hôn / bình minh)',
  SEASON_TRIM: 'Theo mùa + cắt bớt giữa đêm',
  MIDNIGHT_DIM: 'Giảm sáng khuya',
  CUSTOM: 'Tự định nghĩa',
};

export interface StrategySpec {
  outputs?: number[];
  windows?: { onMin: number; offMin: number; daysOfWeek?: string }[];
  sunsetOffsetMin?: number;
  sunriseOffsetMin?: number;
  trimFromMin?: number;
  trimToMin?: number;
  trimOutputs?: number[];
  dimFromMin?: number;
  dimToMin?: number;
  dimPercent?: number;
  specialDays?: SpecialDay[];
}

/**
 * ➕ `GD-08` 11/09/2026 — MỘT TUYẾN ĐÃ ĐƯỢC GẮN CHIẾN LƯỢC NÀY (`RouteStrategy`).
 *
 * 🔴 `appliedAt === null` = **ĐÃ GẮN, CHƯA ÁP LẦN NÀO** — KHÁC "đã áp". Máy chủ trả nguyên
 *   `null` xuống chứ không thay bằng `createdAt` cho gọn, để giao diện phân biệt được hai câu
 *   ấy (Luật số 0). Gộp chúng là khoe một lần áp chưa bao giờ xảy ra.
 * ⛔ `tenDayDu` do MÁY CHỦ dựng (`services/conduong.ts`) — giao diện KHÔNG tự ghép tên + địa
 *   chỉ, vì hai chỗ ghép là hai chỗ sẽ trôi khỏi nhau (chốt YC-21).
 */
export interface RouteStrategyLink {
  routeId: string;
  tenDayDu: string;
  appliedAt: string | null;
}

export interface Strategy {
  id: string;
  projectId: string;
  name: string;
  kind: StrategyKind;
  description: string | null;
  spec: StrategySpec;
  createdAt: string;
  updatedAt: string;
  /**
   * ⚠ CÓ THỂ VẮNG khi nói chuyện với một bản MÁY CHỦ CŨ (trường này thêm ở `GD-08`). `undefined`
   *   = *"máy chủ không nói"*, `[]` = *"máy chủ nói: chưa tuyến nào"*. Hai câu khác nhau, và
   *   giao diện phải vẽ khác nhau — đúng cái bẫy `null` ≠ `[]` đã cắt nhiều lần.
   */
  routeLinks?: RouteStrategyLink[];
}

export interface StrategyPreview {
  schedule: FullSchedule;
  notes: string[];
  v1Preview: V1Preview;
}

export interface StrategyApplyResultItem {
  cabinetId: string;
  code: string;
  ok: boolean;
  blocked?: boolean;
  losses?: string[];
  notes?: string[];
  state?: CommandState;
  error?: string;
}

export interface StrategyApplyResult {
  total: number;
  applied: number;
  blocked: number;
  results: StrategyApplyResultItem[];
}

/**
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * TRẠNG THÁI MÔ PHỎNG NGÕ VÀO — hình dạng của `GET /api/mo-phong/trang-thai`.
 *
 * ⚠ `dau` do MÁY CHỦ gửi xuống, không phải chuỗi giao diện tự gõ. Cùng lý do đã ghi ở
 *   `api/mophong.ts`: `[MÔ PHỎNG]` là DẤU PHÂN BIỆT THẬT/GIẢ, mà một dấu có hai nơi khai là
 *   một dấu sẽ lệch — và ngày nó lệch thì chỗ lệch chính là chỗ người đọc mất khả năng phân
 *   biệt. Một chuỗi, một nguồn, hai đầu (cùng luật với `METER_HET_TUOI_MS`).
 * ═══════════════════════════════════════════════════════════════════════════════════════
 */
export interface TrangThaiMoPhong {
  bat: boolean;
  dau: string;
  ngoVao: Array<{ khoa: string; ma: string; ten: string; viec: string }>;
}

/** TK-19 — `/api/firmware/thong-ke`: tủ nhóm theo bản firmware, kèm TUỔI lần khai. */
export interface NhomBanFirmware {
  git: string | null; dirty: boolean | null; soTu: number; soOnline: number;
  fwLucMoiNhat: string | null; fwLucCuNhat: string | null; tuoiMoiNhatMs: number | null; tuoiCuNhatMs: number | null;
  tu: Array<{ id: string; hardwareId: string; name: string; online: boolean; fwLuc: string | null; tuoiKhaiMs: number | null }>;
}
export interface ThongKeFirmware { luc: string; tongTu: number; soChuaBiet: number; nhom: NhomBanFirmware[] }
